import React, { useEffect, useState } from 'react';
import { IndianRupee, TrendingUp, Clock, Users, CheckCircle2 } from 'lucide-react';
import { Card, StatCard, StatSkeleton, ActionBar } from '../ui';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { useBookings } from '../context/BookingContext';
import { formatCurrency } from '../utils';
import { fetchLawyerDashboard } from '../services/earningsService';

export const DashboardPage = ({ onNavigate }) => {
  const { user } = useAuth();
  const { bookings } = useBookings();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  // ── Load real stats from API ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setStatsLoading(true);
      setStatsError('');
      try {
        const data = await fetchLawyerDashboard();
        if (!cancelled) setStats(data);
      } catch (err) {
        if (!cancelled) setStatsError(err?.response?.data?.message || err?.message || 'Failed to load stats');
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    load();
    window.addEventListener('lawyer:booking-updated', load);
    return () => {
      cancelled = true;
      window.removeEventListener('lawyer:booking-updated', load);
    };
  }, []);

  // ── Profile completeness ────────────────────────────────────────────────────
  const isProfileComplete = Boolean(
    user?.name?.trim() &&
    user?.email?.trim() &&
    user?.phone?.trim() &&
    user?.workplace?.trim() &&
    user?.barCouncilNo?.trim() &&
    user?.specialization?.trim()
  );

  // ── Today's schedule from BookingContext (real bookings already loaded) ──────
  const todayStr = new Date().toISOString().slice(0, 10);
  const todaysSchedule = bookings
    .filter((b) => b.date === todayStr && b.time)
    .sort((a, b) => (a.time > b.time ? 1 : -1))
    .slice(0, 5);

  // ── Recent bookings from API stats (top 5) ──────────────────────────────────
  const recentBookings = stats?.recentBookings || bookings.slice(0, 5);

  const todayLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div>
      <PageHeader
        title={`Hello, ${user?.name ? user.name.replace('Adv. ', '') : 'Advocate'}`}
        subtitle={`Here's your overview for today, ${todayLabel}`}
        showBack={false}
      />

      <div>
        {/* Profile completeness banner */}
        <div className={`mb-6 rounded-2xl px-5 py-4 font-medium border ${
          isProfileComplete
            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
            : 'border-amber-200 bg-amber-50 text-amber-800'
        }`}>
          {isProfileComplete
            ? "✓ Profile Complete — You're ready to accept consultations."
            : 'Your profile is not complete. Please complete all details in profile setup.'}
        </div>

        {/* Stats error banner */}
        {statsError && (
          <div className="mb-4 rounded-xl px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm">
            ⚠ Could not load stats: {statsError}
          </div>
        )}

        {/* ── Booking stats row ── */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-5">
          {statsLoading ? (
            <>
              <StatSkeleton /><StatSkeleton /><StatSkeleton />
            </>
          ) : (
            <>
              <StatCard
                label="Total Bookings"
                value={stats?.totalBookings ?? 0}
                icon={Users}
                color="slate"
                onClick={() => onNavigate('bookings')}
              />
              <StatCard
                label="Pending Requests"
                value={stats?.pendingRequests ?? 0}
                icon={Clock}
                color="amber"
                sub="Awaiting your response"
                onClick={() => onNavigate('bookings', { filter: 'pending' })}
              />
              <StatCard
                label="Ongoing Cases"
                value={stats?.ongoingCases ?? 0}
                icon={CheckCircle2}
                color="emerald"
                sub="Accepted bookings"
                onClick={() => onNavigate('cases', { filter: 'active' })}
              />
            </>
          )}
        </div>

        {/* ── Earnings row ── */}
        <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {statsLoading ? (
            <>
              <StatSkeleton /><StatSkeleton /><StatSkeleton /><StatSkeleton />
            </>
          ) : (
            <>
              <StatCard
                label="Monthly Earnings"
                value={formatCurrency(stats?.monthlyEarnings ?? 0)}
                icon={TrendingUp}
                color="emerald"
                sub="This month (after platform fee)"
              />
              <StatCard
                label="Total Earnings"
                value={formatCurrency(stats?.totalEarnings ?? 0)}
                icon={IndianRupee}
                color="blue"
                sub="All time"
              />
              <StatCard
                label="Upcoming Earnings"
                value={formatCurrency(stats?.upcomingEarnings ?? 0)}
                icon={CheckCircle2}
                color="blue"
                sub="From accepted bookings"
              />
              <StatCard
                label="Pending Earnings"
                value={formatCurrency(stats?.pendingEarnings ?? 0)}
                icon={Clock}
                color="amber"
                sub="From pending bookings"
              />
            </>
          )}
        </div>

        {/* ── Recent bookings + Today's schedule ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Booking Requests */}
          <Card className="p-0">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Recent Booking Requests</h3>
                <p className="text-slate-500 text-sm">
                  {statsLoading ? '…' : `${stats?.pendingRequests ?? 0} pending`}
                </p>
              </div>
              <button
                className="text-blue-600 text-sm font-semibold hover:underline"
                onClick={() => onNavigate('bookings')}
              >
                View all →
              </button>
            </div>
            <div className="p-4 space-y-3">
              {recentBookings.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No bookings yet</p>
              ) : (
                recentBookings.map((booking, idx) => {
                  // Works whether it's a raw DB record or a mapped booking
                  const name = booking.clientName || booking.client_name || 'Client';
                  const type = booking.caseType || booking.case_type || 'Legal Consultation';
                  const status = String(booking.status || 'pending').toLowerCase();
                  const statusColors = {
                    pending: 'bg-amber-50 text-amber-700 border-amber-200',
                    accepted: 'bg-blue-50 text-blue-700 border-blue-200',
                    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                    rejected: 'bg-red-50 text-red-700 border-red-200',
                    cancelled: 'bg-slate-50 text-slate-500 border-slate-200',
                  };
                  return (
                    <div
                      key={booking._id || booking.id || idx}
                      className="flex items-center justify-between border border-slate-100 rounded-xl p-3 bg-white"
                    >
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{name}</p>
                        <p className="text-slate-500 text-xs mt-0.5">{type}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusColors[status] || statusColors.pending}`}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </Card>

          {/* Today's Schedule */}
          <Card className="p-0">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900">Today's Schedule</h3>
              <p className="text-slate-500 text-sm">{todayLabel.split(',')[0]}</p>
            </div>
            <div className="p-6 space-y-4">
              {todaysSchedule.length === 0 ? (
                <p className="text-slate-400 text-sm text-center py-4">No consultations scheduled today</p>
              ) : (
                todaysSchedule.map((booking) => (
                  <div key={booking.id} className="flex gap-4 items-start">
                    <p className="w-16 font-bold text-slate-800 text-sm flex-shrink-0">{booking.time || '--:--'}</p>
                    <div className="border-l-2 border-blue-500 pl-3">
                      <p className="font-bold text-sm">{booking.clientName}</p>
                      <p className="text-slate-500 text-xs">
                        {booking.caseType}
                        {booking.consultation_fee ? ` · ₹${booking.consultation_fee}` : ''}
                        {booking.meetingLink ? ' · Google Meet' : ''}
                      </p>
                      {booking.meetingLink && (
                        <a
                          href={booking.meetingLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Join meeting
                        </a>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div className="pt-4 border-t border-slate-200">
                <ActionBar>
                  <button
                    onClick={() => onNavigate('schedule')}
                    className="border border-slate-300 bg-white text-slate-800 font-bold hover:bg-black hover:text-white hover:border-black transition-colors"
                  >
                    Open Schedule
                  </button>
                  <button
                    onClick={() => onNavigate('chat')}
                    className="border border-slate-300 bg-white text-slate-800 font-bold hover:bg-black hover:text-white hover:border-black transition-colors"
                  >
                    Open Chat
                  </button>
                </ActionBar>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
